/**
 * Cloudflare KV API initialization utility
 * Automatically loads and initializes the Cloudflare API from environment variables
 */

import {
  initCloudflareApi,
  getConfig,
  checkProxyConnection,
} from "./cloudflareApi";

// Debug flag - we'll use this in console logs
const DEBUG = true;

/**
 * Initialize Cloudflare from environment variables
 */
export const initCloudflareFromEnv = async (): Promise<boolean> => {
  try {
    if (DEBUG)
      console.log(
        "🔄 Attempting to load Cloudflare config from environment variables"
      );

    const accountId = process.env.REACT_APP_CLOUDFLARE_ACCOUNT_ID;
    const namespaceId = process.env.REACT_APP_CLOUDFLARE_NAMESPACE_ID;
    const apiToken = process.env.REACT_APP_CLOUDFLARE_API_TOKEN;
    const proxyUrl = process.env.REACT_APP_PROXY_URL || "http://localhost:4000";

    console.log("📊 Environment variables loaded:", {
      accountId: accountId ? `✓ ${accountId.substring(0, 4)}...` : "✗ missing",
      namespaceId: namespaceId
        ? `✓ ${namespaceId.substring(0, 4)}...`
        : "✗ missing",
      apiToken: apiToken ? "✓ present" : "✗ missing",
      proxyUrl: proxyUrl || "not set",
    });

    if (!accountId || !namespaceId || !apiToken) {
      console.error(
        "❌ Missing Cloudflare configuration in environment variables"
      );

      // Log which specific variables are missing
      if (!accountId)
        console.error("❌ Missing REACT_APP_CLOUDFLARE_ACCOUNT_ID");
      if (!namespaceId)
        console.error("❌ Missing REACT_APP_CLOUDFLARE_NAMESPACE_ID");
      if (!apiToken) console.error("❌ Missing REACT_APP_CLOUDFLARE_API_TOKEN");

      return false;
    }

    initCloudflareApi({
      accountId,
      namespaceId,
      apiToken,
      proxyUrl,
    });

    // Verify configuration was set correctly
    const config = getConfig();
    if (!config) {
      console.error(
        "❌ Failed to initialize Cloudflare API - getConfig() returned null after initialization"
      );
      return false;
    }

    // Check if proxy server is available
    try {
      console.log("🔄 Checking proxy connection...");
      const proxyAvailable = await checkProxyConnection();
      if (!proxyAvailable) {
        console.error(
          `❌ Proxy server at ${proxyUrl} is not available. Cloudflare KV operations will fail.`
        );
        console.error(
          "❌ Make sure the proxy server is running and accessible."
        );
        // We still return true because the config itself was initialized correctly
      } else {
        console.log(`✅ Proxy server at ${proxyUrl} is available.`);
      }
    } catch (error) {
      console.error("❌ Error checking proxy connection:", error);
      // We still return true because the config itself was initialized correctly
    }

    console.log("✅ Cloudflare KV API initialized from environment variables");
    return true;
  } catch (error) {
    console.error(
      "❌ Error initializing Cloudflare API from environment:",
      error
    );
    return false;
  }
};

// Keep legacy function for backward compatibility
export const initCloudflareFromStorage = async (): Promise<boolean> => {
  try {
    console.log("🔄 Attempting to load Cloudflare config from localStorage");

    const configString = localStorage.getItem("cloudflareConfig");
    if (!configString) {
      console.log("⚠️ No Cloudflare configuration found in localStorage");
      // Try to initialize from environment variables as fallback
      console.log("🔄 Falling back to environment variables");
      return await initCloudflareFromEnv();
    }

    const config = JSON.parse(configString);
    console.log("📊 Configuration loaded from localStorage:", {
      accountId: config.accountId
        ? `✓ ${config.accountId.substring(0, 4)}...`
        : "✗ missing",
      namespaceId: config.namespaceId
        ? `✓ ${config.namespaceId.substring(0, 4)}...`
        : "✗ missing",
      apiToken: config.apiToken ? "✓ present" : "✗ missing",
    });

    if (!config.accountId || !config.namespaceId || !config.apiToken) {
      console.log(
        "⚠️ Incomplete Cloudflare configuration found in localStorage"
      );
      // Log which specific fields are missing
      if (!config.accountId)
        console.error("❌ Missing accountId in localStorage config");
      if (!config.namespaceId)
        console.error("❌ Missing namespaceId in localStorage config");
      if (!config.apiToken)
        console.error("❌ Missing apiToken in localStorage config");

      // Try to initialize from environment variables as fallback
      console.log("🔄 Falling back to environment variables");
      return await initCloudflareFromEnv();
    }

    const proxyUrl = process.env.REACT_APP_PROXY_URL || "http://localhost:4000";
    console.log(`📊 Using proxy URL: ${proxyUrl}`);

    initCloudflareApi({
      accountId: config.accountId,
      namespaceId: config.namespaceId,
      apiToken: config.apiToken,
      proxyUrl,
    });

    // Verify configuration was set correctly
    const verifyConfig = getConfig();
    if (!verifyConfig) {
      console.error(
        "❌ Failed to initialize Cloudflare API - getConfig() returned null after initialization"
      );
      console.log("🔄 Falling back to environment variables");
      return await initCloudflareFromEnv();
    }

    // Check if proxy server is available
    try {
      console.log("🔄 Checking proxy connection...");
      const proxyAvailable = await checkProxyConnection();
      if (!proxyAvailable) {
        console.error(
          `❌ Proxy server at ${proxyUrl} is not available. Cloudflare KV operations will fail.`
        );
        console.error(
          "❌ Make sure the proxy server is running and accessible."
        );
        // We still return true because the config itself was initialized correctly
      } else {
        console.log(`✅ Proxy server at ${proxyUrl} is available.`);
      }
    } catch (error) {
      console.error("❌ Error checking proxy connection:", error);
      // We still return true because the config itself was initialized correctly
    }

    console.log("✅ Cloudflare KV API initialized from stored configuration");
    return true;
  } catch (error) {
    console.error(
      "❌ Error initializing Cloudflare API from localStorage:",
      error
    );
    // Try to initialize from environment variables as fallback
    console.log("🔄 Falling back to environment variables due to error");
    return await initCloudflareFromEnv();
  }
};
